// Phase 4.5B — Reusable 3-tier ascension card.
// Used by /audit/next-steps/:sessionId for Level 1 (Audit, completed),
// Level 2 (Consultation, recommended), Level 3 (Enterprise).
// All copy is owned by the parent page (NextSteps.jsx); this component
// is pure presentation. CTA semantics are decided by `ctaVariant`:
//   - "primary"   → .sf-btn      (Level 2 — RECOMMENDED tier)
//   - "secondary" → .sf-btn-secondary (Levels 1 + 3)
//
// `badge` is one of "COMPLETED" | "RECOMMENDED" | null.
export default function AscensionTierCard({
  variant,            // "level-1" | "level-2" | "level-3"
  badge,              // "COMPLETED" | "RECOMMENDED" | null
  title,
  price,
  subtitle,
  body,
  boundary,           // optional boundary string (Level 2 only)
  ctaLabel,
  ctaVariant,         // "primary" | "secondary"
  onCtaClick,
}) {
  const isRecommended = variant === "level-2";
  const border = isRecommended ? "var(--sf-gold)" : "var(--sf-gold-dim)";
  const btnClass = ctaVariant === "primary" ? "sf-btn" : "sf-btn-secondary";

  return (
    <article
      className="sf-card"
      style={{
        borderColor: border,
        marginBottom: 20,
        position: "relative",
      }}
      data-testid={`tier-${variant}`}
    >
      {badge && (
        <div
          className="sf-mono"
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            fontSize: 11,
            letterSpacing: "0.14em",
            color: isRecommended ? "var(--sf-gold)" : "var(--sf-muted)",
            border: `1px solid ${isRecommended ? "var(--sf-gold)" : "var(--sf-gold-dim)"}`,
            padding: "4px 10px",
            borderRadius: 2,
            textTransform: "uppercase",
          }}
          data-testid={`tier-${variant}-badge`}
        >
          {badge}
        </div>
      )}

      <div className="sf-eyebrow" style={{ marginBottom: 10 }} data-testid={`tier-${variant}-eyebrow`}>
        {variant === "level-1" && "LEVEL 1"}
        {variant === "level-2" && "LEVEL 2"}
        {variant === "level-3" && "LEVEL 3"}
      </div>

      <h2
        className="sf-display"
        style={{ fontSize: 26, lineHeight: 1.2, marginBottom: 8 }}
        data-testid={`tier-${variant}-title`}
      >
        {title}
      </h2>

      <div
        className="sf-mono"
        style={{ fontSize: 22, color: "var(--sf-gold)", marginBottom: 6, letterSpacing: "0.02em" }}
        data-testid={`tier-${variant}-price`}
      >
        {price}
      </div>

      <p
        className="sf-muted-text"
        style={{ fontSize: 14, marginBottom: 16, letterSpacing: "0.01em" }}
        data-testid={`tier-${variant}-subtitle`}
      >
        {subtitle}
      </p>

      <p
        className="sf-muted-text"
        style={{ marginBottom: boundary ? 14 : 22, lineHeight: 1.7, maxWidth: 680 }}
        data-testid={`tier-${variant}-body`}
      >
        {body}
      </p>

      {boundary && (
        <p
          className="sf-mono"
          style={{
            fontSize: 12,
            color: "var(--sf-muted)",
            letterSpacing: "0.08em",
            marginBottom: 22,
            textTransform: "uppercase",
          }}
          data-testid={`tier-${variant}-boundary`}
        >
          {boundary}
        </p>
      )}

      <button
        type="button"
        className={btnClass}
        onClick={onCtaClick}
        data-testid={`tier-${variant}-cta`}
      >
        {ctaLabel}
      </button>
    </article>
  );
}
